import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Alert,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  NotificationsActive,
  Email,
  WhatsApp,
  Save,
  Schedule,
  Send
} from '@mui/icons-material';
import { apiService } from '../../services/api';

interface NotificationSettings {
  notificationChannel: 'email' | 'whatsapp' | 'both';
  defaultReminderEnabled: boolean;
  defaultReminderMinutes: number;
  email: string | null;
  phone: string | null;
  emailConfigured: boolean;
  whatsappConfigured: boolean;
}

const REMINDER_OPTIONS = [
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 120, label: '2 hours before' },
  { value: 1440, label: '1 day before' },
];

const NotificationPreferences: React.FC = () => {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingWhatsApp, setTestingWhatsApp] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await apiService.getNotificationSettings();
      if (response.success) {
        setSettings(response.settings);
      }
    } catch (err: any) {
      setError('Failed to load notification settings');
      console.error('Load settings error:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await apiService.updateNotificationSettings({
        notificationChannel: settings.notificationChannel,
        defaultReminderEnabled: settings.defaultReminderEnabled,
        defaultReminderMinutes: settings.defaultReminderMinutes
      });

      if (response.success) {
        setSuccess('Settings saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      setError('Failed to save settings');
      console.error('Save settings error:', err);
    } finally {
      setSaving(false);
    }
  };

  const testEmail = async () => {
    try {
      setTestingEmail(true);
      setError('');
      setSuccess('');
      const response = await apiService.testEmailNotification();
      if (response.success) {
        setSuccess('Test email sent successfully! Check your inbox.');
      } else {
        setError(response.error || 'Failed to send test email');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const testWhatsApp = async () => {
    try {
      setTestingWhatsApp(true);
      setError('');
      setSuccess('');
      const response = await apiService.testWhatsAppNotification();
      if (response.success) {
        setSuccess('Test WhatsApp message sent! Check your phone.');
      } else {
        setError(response.error || 'Failed to send test WhatsApp');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send test WhatsApp');
    } finally {
      setTestingWhatsApp(false);
    }
  };

  const handleChange = (field: keyof NotificationSettings, value: any) => {
    if (!settings) return;
    setSettings({ ...settings, [field]: value });
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <NotificationsActive color="primary" />
        Notification Preferences
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Notification Channel */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Notification Channel
        </Typography>
        
        <FormControl fullWidth>
          <InputLabel>Send reminders via</InputLabel>
          <Select
            value={settings?.notificationChannel || 'whatsapp'}
            label="Send reminders via"
            onChange={(e) => handleChange('notificationChannel', e.target.value)}
          >
            <MenuItem value="whatsapp">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatsApp sx={{ color: '#25D366' }} />
                WhatsApp only
              </Box>
            </MenuItem>
            <MenuItem value="email">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email color="primary" />
                Email only
              </Box>
            </MenuItem>
            <MenuItem value="both">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsActive color="secondary" />
                Both Email & WhatsApp
              </Box>
            </MenuItem>
          </Select>
        </FormControl>

        {/* Contact Info */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {settings?.emailConfigured ? (
            <Chip 
              icon={<Email />} 
              label={settings.email} 
              color="success" 
              variant="outlined" 
              size="small"
            />
          ) : (
            <Chip 
              icon={<Email />} 
              label="Email not configured" 
              color="warning" 
              variant="outlined" 
              size="small"
            />
          )}
          
          {settings?.whatsappConfigured ? (
            <Chip 
              icon={<WhatsApp />} 
              label={settings.phone} 
              color="success" 
              variant="outlined" 
              size="small"
            />
          ) : (
            <Chip 
              icon={<WhatsApp />} 
              label="Phone not configured (update in Profile tab)" 
              color="warning" 
              variant="outlined" 
              size="small"
            />
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Default Reminder Settings */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          <Schedule sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
          Default Reminder Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These settings apply to all new tasks unless overridden per task.
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={settings?.defaultReminderEnabled || false}
              onChange={(e) => handleChange('defaultReminderEnabled', e.target.checked)}
              color="primary"
            />
          }
          label="Enable reminders by default"
          sx={{ mb: 2 }}
        />

        {settings?.defaultReminderEnabled && (
          <FormControl fullWidth>
            <InputLabel>Default reminder time</InputLabel>
            <Select
              value={settings?.defaultReminderMinutes || 60}
              label="Default reminder time"
              onChange={(e) => handleChange('defaultReminderMinutes', e.target.value)}
            >
              {REMINDER_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Test Notifications */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          <Send sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
          Test Notifications
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={testEmail}
            disabled={testingEmail || !settings?.emailConfigured}
            startIcon={testingEmail ? <CircularProgress size={16} /> : <Email />}
          >
            {testingEmail ? 'Sending...' : 'Test Email'}
          </Button>
          
          <Button
            variant="outlined"
            onClick={testWhatsApp}
            disabled={testingWhatsApp || !settings?.whatsappConfigured}
            startIcon={testingWhatsApp ? <CircularProgress size={16} /> : <WhatsApp />}
            sx={{ 
              borderColor: '#25D366', 
              color: '#25D366',
              '&:hover': { borderColor: '#128C7E', backgroundColor: 'rgba(37, 211, 102, 0.04)' }
            }}
          >
            {testingWhatsApp ? 'Sending...' : 'Test WhatsApp'}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Info Box */}
      <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, mb: 3 }}>
        <Typography variant="body2" color="info.dark">
          <strong>How reminders work:</strong>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            <li>Reminders are sent before scheduled task time</li>
            <li>WhatsApp requires your phone number in Profile tab</li>
            <li>You can override settings for individual tasks</li>
          </ul>
        </Typography>
      </Box>

      {/* Save Button */}
      <Button
        variant="contained"
        onClick={saveSettings}
        disabled={saving}
        startIcon={saving ? <CircularProgress size={20} /> : <Save />}
        fullWidth
      >
        {saving ? 'Saving...' : 'Save Preferences'}
      </Button>
    </Paper>
  );
};

export default NotificationPreferences;
